import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { invoicesApi, clientsApi, extractList, INVOICE_STATUS_OPTIONS, type InvoiceStatus } from '../../api/facades';

const InvoicesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState<number | ''>('');
  const [invStatus, setInvStatus] = useState<InvoiceStatus | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: clientsRes } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list({}),
  });
  const clients = extractList(clientsRes);

  const { data: res, isLoading } = useQuery({
    queryKey: ['invoices', clientId, invStatus],
    queryFn: () =>
      invoicesApi.list({
        clientId: typeof clientId === 'number' ? clientId : undefined,
        invStatus: invStatus || undefined,
      }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => invoicesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setDeleteId(null);
      toast.success('Invoice deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'invoice_no', label: 'Invoice No.' },
    { key: 'client', label: 'Client', render: (r: any) => r.client?.name ?? r.client_id ?? '—' },
    { key: 'total_amount', label: 'Total' },
    { key: 'inv_status', label: 'Status' },
    { key: 'due_date', label: 'Due date', render: (r: any) => (r.due_date ? String(r.due_date).slice(0, 10) : '—') },
  ];

  return (
    <div>
      <PageHeader
        hideTitle
        subtitle="Manage invoices."
        primaryAction={{ label: 'New', to: '/dashboard/invoices/new' }}
      />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
          <select
            value={clientId === '' ? '' : clientId}
            onChange={(e) => setClientId(e.target.value === '' ? '' : Number(e.target.value))}
            className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
          >
            <option value="">All clients</option>
            {clients.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={invStatus}
            onChange={(e) => setInvStatus((e.target.value || '') as InvoiceStatus | '')}
            className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
          >
            <option value="">All statuses</option>
            {INVOICE_STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          emptyMessage="No invoices."
          onRowClick={(id) => navigate(`/dashboard/invoices/${id}`)}
          onEdit={(id) => navigate(`/dashboard/invoices/${id}/edit`)}
          onDelete={(id) => setDeleteId(Number(id))}
        />
      </div>
      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)}
        title="Delete invoice"
        message="Are you sure? This cannot be undone."
        loading={deleteMu.isPending}
      />
    </div>
  );
};

export default InvoicesListPage;
